import * as Sentry from '@sentry/react';
import { action, autorun, computed, observable, reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router';

import autobind from 'autobind-decorator';
import { withConfig, withI18n } from 'spa/Components/Context';
import {
  BIO_TAB_NAME,
  INFO_TAB_NAME,
  MIN_REVIEW_COUNTER,
  NUMBER_OF_REVIEWS_PER_REQUEST,
  REVIEWS_TAB_NAME,
} from 'spa/Components/Reviews/values';
import { BOOKING_BASE_URL } from 'utils/constants';
import isValidException from 'utils/isValidException';

import BreadcrumbItem, { BreadcrumbColorTheme } from '@urbanmassage/component-library/lib/Breadcrumbs/BreadcrumbItem/BreadcrumbItem';
import BreadcrumbsContainer from '@urbanmassage/component-library/lib/Breadcrumbs/BreadcrumbsContainer/BreadcrumbsContainer';
import Button, {
  ButtonSizeTypes,
  ButtonThemeTypes,
} from '@urbanmassage/component-library/lib/Button/Button';
import ContentBox, { ContentBoxColorThemes } from '@urbanmassage/component-library/lib/ContentBox/ContentBox';
import Divider from '@urbanmassage/component-library/lib/Divider/Divider';
import Certified from '@urbanmassage/component-library/lib/Icons/Certified/Certified';
import { IconSizes } from '@urbanmassage/component-library/lib/Theme/b2c-theme';
import ProfileHeading from '@urbanmassage/component-library/lib/ProfileHeading/ProfileHeading';
import Typography, { TypeVariants } from '@urbanmassage/component-library/lib/Typography/Typography';
import NotFound from 'spa/Pages/NotFound';

import DocumentMeta from 'spa/Components/DocumentMeta';
import DocumentTitle from 'spa/Components/DocumentTitle';
import TextReviews from 'spa/Components/Reviews/TextReviews';
import WorkerReviewsLong from 'spa/Components/Reviews/WorkerReviewsLong';
import TherapistFactsGrid from './TherapistFactsGrid';
import TherapistProfileTabs from './TherapistProfileTabs';
import TherapistSingleLoading from './TherapistSingleLoading';
import TherapistTopInfo from './TherapistTopInfo';

import './therapist-single.less';

const isServer = typeof window === 'undefined';

const THERAPIST_DESKTOP_BREAKPOINT = '800px';

@withRouter
@inject(({
  store: {
    address,
    basketNew,
    notifications,
    screen,
    selection,
    specialities,
    tracker,
    userAgent,
    workerLanguages,
    workerPromotedReviews,
    workerReviews,
    workers,
  }, therapistsState, params,
}) => (
  {
    address,
    basketNew,
    languages: therapistsState.workerLanguages.getOne(params.id),
    params,
    notifications,
    screen,
    selection,
    specialities,
    state: therapistsState.workers.getOne(params.id),
    promotedReviews: therapistsState.workerPromotedReviews.getOne(params.id),
    reviews: therapistsState.workerReviews.getSearch({
      offset: 0,
      limit: NUMBER_OF_REVIEWS_PER_REQUEST,
      id: params.id,
    }, params.id),
    therapistsState,
    tracker,
    userAgent,
    workerLanguages,
    workerReviews,
    workerPromotedReviews,
    workers,
  }))
@observer
@withConfig
@withI18n
class TherapistSingle extends React.Component {
  static propTypes = {
    address: PropTypes.shape({
      isLoading: PropTypes.bool.isRequired,
      load: PropTypes.func.isRequired,
      response: PropTypes.shape({
        serviceArea: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.object,
        ]).isRequired,
      }),
      error: PropTypes.object,
    }).isRequired,
    basketNew: PropTypes.shape({
      basketsAppointmentWorkerUpdate: PropTypes.shape({
        put: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
    languages: PropTypes.object,
    location: PropTypes.object.isRequired,
    notifications: PropTypes.shape({
      add: PropTypes.func.isRequired,
    }).isRequired,
    params: PropTypes.object.isRequired,
    promotedReviews: PropTypes.object,
    reviews: PropTypes.object,
    router: PropTypes.object.isRequired,
    screen: PropTypes.object.isRequired,
    selection: PropTypes.object.isRequired,
    specialities: PropTypes.shape({
      getOneInstance: PropTypes.func.isRequired,
      maybeLoadOne: PropTypes.func.isRequired,
      models: PropTypes.object,
    }).isRequired,
    state: PropTypes.object,
    therapistsState: PropTypes.shape({
      filtersApplied: PropTypes.bool,
      fullReviewsList: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.object,
      ]).isRequired,
      isProfileLoading: PropTypes.bool,
      languages: PropTypes.object,
      offset: PropTypes.number.isRequired,
      promotedReviews: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.object,
      ]),
      reviewsList: PropTypes.object,
      selectedId: PropTypes.string,
      selection: PropTypes.object.isRequired,
      workerDetails: PropTypes.object,
      workerPromotedReviewsList: PropTypes.arrayOf(PropTypes.object),
      therapistSpecialities: PropTypes.arrayOf(PropTypes.object),
      totalReviews: PropTypes.number.isRequired,
      workerBio: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.string,
      ]),
      workerLanguages: PropTypes.shape({
        maybeLoadOne: PropTypes.func.isRequired,
      }).isRequired,
      workerPromotedReviews: PropTypes.shape({
        maybeLoadOne: PropTypes.func.isRequired,
      }).isRequired,
      workerReviews: PropTypes.shape({
        getSearch: PropTypes.func.isRequired,
        maybeLoadSearch: PropTypes.func.isRequired,
      }).isRequired,
      workers: PropTypes.shape({
        getOne: PropTypes.func.isRequired,
        maybeLoadOne: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
    userAgent: PropTypes.string.isRequired,
  };

  UNSAFE_componentWillMount() {
    const {config, i18n} = this.context;
    const {
      params,
      selection,
      specialities,
      therapistsState,
    } = this.props;
    const {
      workerPromotedReviews,
      workerReviews,
      workers,
    } = therapistsState;

    if (specialities.models) {
      specialities.models.clear();
    }

    this.props.therapistsState.selectedId = params.id;

    this.showReviews = config.getValue('b2c.locale.text-reviews-enabled', i18n.umRegion);

    this.instagramActive = config.getValue('b2c.locale.instagram-therapist-profile-site-active', i18n.umRegion);

    selection.treatmentInfoViewing = undefined;

    workers.maybeLoadOne(this.props.params.id);
    workerPromotedReviews.maybeLoadOne(this.props.params.id);

    workerReviews.maybeLoadSearch({
      offset: 0,
      limit: NUMBER_OF_REVIEWS_PER_REQUEST,
      id: params.id,
    }, params.id);
  }

  componentDidMount() {
    this.listeners = [
      autorun(() => {
        const {selection} = this.props.therapistsState;
        if (!selection.date) {
          selection.date = moment().format(selection.DATE_FORMAT);
        }
      }, {
        name: 'pre set current date if not already chosen',
      }),
      autorun(() => {
        if (this.props.state && this.props.state.instance) {
          const {instance} = this.props.state;

          const {tracker} = this.props;
          tracker.track(`Viewed Therapist single page`, {
            category: 'Workers',
            label: instance.name,
            worker: {
              id: instance.id,
              name: instance.name,
            },
          });
        }
      }, {
        name: 'trigger viewed page tracking event',
      }),
      reaction(
        () => this.props.therapistsState && this.props.therapistsState.selection && this.props.therapistsState.selection.treatmentInfoViewing,
        (treatmentInfoViewing) => {
          if (treatmentInfoViewing) {
            this.props.specialities.maybeLoadOne(treatmentInfoViewing);
          }
        },
        {
          fireImmediately: true,
          name: 'Get details of clicked treatment',
        },
      ),
      reaction(
        () => this.props.state && this.props.state.instance,
        (instance) => {
          if (instance) {
            this.props.therapistsState.workerLanguages.maybeLoadOne(this.props.params.id);
          }
        },
        {
          fireImmediately: true,
          name: 'Get worker languages',
        },
      ),
    ];
  }

  componentWillUnmount() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @observable currentTabMobile = INFO_TAB_NAME;
  @observable showReviews;

  @computed get formatServiceAreas() {
    const {therapistInfo} = this;
    if (!therapistInfo) return null;
    const {instance} = therapistInfo;

    if (!instance) return null;
    const {serviceAreas} = instance;
    if (serviceAreas && serviceAreas.length) {
      return serviceAreas.map(area => (
        area.id
      ));
    }

    return null;
  }

  /*
   * Therapist content is localised so we should only show hreflangs for the therapists supported
   * Locale / city / service area
   */
  @computed get supportedHrefLangs() {
    const {formatServiceAreas} = this;
    const supported = [];
    /*
     * If there are no service areas then it will be a therapist from an old city that has been shutdown
     * We should therefore default to en-gb
     */
    if (!formatServiceAreas) return ['en-gb'];
    // All service areas start with a code like AT- / FR- / UK- or LON lets filter them here
    // and create an array of unique codes for the supported service areas
    const codes = [...new Set(formatServiceAreas.map(area => area && area.substr(0, 3)))];

    // If we have some service area codes lets create an array of hreflangs to support
    if (codes && codes.length) {
      codes.forEach((code) => {
        if (code === 'LON' || code === 'UK-') {
          supported.push('en-gb');
        }
        if (code === 'FR-') {
          supported.push('fr-fr');
        }
      });
    }

    return supported;
  }

  @computed get therapistInfo() {
    setTimeout(() => this.props.therapistsState.workers.maybeLoadOne(this.props.params.id), 0);
    return this.props.therapistsState.workers.getOne(this.props.params.id);
  }

  @computed get showReviewsTabMobile() {
    const {
      state: {instance = {}} = {instance: {}},
      therapistsState: {
        filtersApplied,
        fullReviewsList,
        reviewsList,
      },
    } = this.props;
    if (!instance) return null;

    const {ratingsBreakdown} = instance;
    if (!ratingsBreakdown) return null;

    const counter = ratingsBreakdown.reduce((count, ratingBreakdown) => {
      count += ratingBreakdown.count; // eslint-disable-line no-param-reassign
      return count;
    }, 0);

    return counter >= MIN_REVIEW_COUNTER ||
      fullReviewsList.length > 0 ||
      (reviewsList && reviewsList.isLoading) ||
      filtersApplied;
  }

  @autobind
  @action('loadMoreReviews - TherapistSingle')
  loadMoreReviews(event) {
    event.preventDefault();

    this.props.therapistsState.offset += NUMBER_OF_REVIEWS_PER_REQUEST;
  }

  @autobind
  @action('onChangeTab - TherapistSingle')
  onChangeTab(event) {
    event.preventDefault();

    const {currentTarget: {parentNode: {dataset}} = {parentNode: {}}} = event;
    const {state: {instance = {}} = {instance: {}}, tracker} = this.props;

    this.currentTabMobile = dataset.tab;

    if (this.currentTabMobile === REVIEWS_TAB_NAME) {
      tracker.track('Clicked on reviews tab - worker profile mobile view', {
        category: 'Workers',
        label: 'Reviews',
        worker: {
          id: instance.id,
        },
      });
    } else {
      tracker.track(`Clicked on ${this.currentTabMobile} tab - worker profile mobile view`, {
        category: 'Workers',
        label: instance.name,
        worker: {
          id: instance.id,
        },
      });
    }
  }

  @autobind
  @action('onClickShowAllReviews - TherapistSingle')
  onClickShowAllReviews() {
    const {tracker} = this.props;
    const {state} = this.props;

    const instance = state && state.instance;

    tracker.track('Clicked on Show All Reviews button', {
      category: 'Workers',
      label: 'Reviews',
      worker: {
        id: instance.id,
      },
    });
  }

  @autobind
  @action('onClickBookWorker - TherapistSingle')
  onClickBookWorker() {
    const {i18n} = this.context;
    const {
      basketNew: {
        basketsAppointmentWorkerUpdate,
      },
      notifications,
      router,
      state,
      tracker,
    } = this.props;

    const instance = state && state.instance;

    tracker.track('Clicked add worker to basket in worker details page', {
      category: '"Therapists profile"',
      label: instance.id,
      worker: {
        id: instance.id,
      },
    });

    basketsAppointmentWorkerUpdate.put({
      worker_id: instance.id,
    })
      .then(() => {
        tracker.track('Successfully added worker to basket in worker details page', {
          category: 'Therapists profile',
          label: instance.id,
          worker: {
            id: instance.id,
          },
        });

        router.push(`/${i18n.lc}/${BOOKING_BASE_URL}/basket`);
      }).catch((err) => {
        if (__DEV__) {
          console.error(err);
        }

        tracker.track('Error adding worker to basket', {
          category: '"Therapists profile"',
          label: instance.id,
          worker: {
            id: instance.id,
          },
        });

        notifications.add({
          id: 'addWorkerToServices',
          type: 'error',
          title: i18n.__error(err),
        });

        if (!i18n.isKnownError(err)) {
          // This is unexpected so record it in Sentry
          if (isValidException(err)) {
            Sentry.captureException(err);
          } else {
            Sentry.captureMessage('Date Worker screen unhandled error adding worker to services');
          }
        }
      });
  }

  @computed get showLoadMoreButton() {
    const {fullReviewsList, totalReviews} = this.props.therapistsState;
    const {showReviews} = this;

    if (!showReviews || !totalReviews || !fullReviewsList) return null;

    return (fullReviewsList.length && fullReviewsList.length < totalReviews);
  }

  @computed get factsIconSizes() {
    const {screen} = this.props;

    return screen.isDesktop ?
      IconSizes.medium :
      IconSizes.small;
  }

  @computed get isDesktopMode() {
    const {screen, userAgent} = this.props;

    if (isServer) {
      return !(screen.mobileDetectionRegex.test(userAgent));
    }

    return screen.isDesktop;
  }

  @computed get loadMoreButtonContent() {
    const {i18n} = this.context;
    const {therapistsState} = this.props;
    const {loadMoreReviews, showLoadMoreButton} = this;

    if (showLoadMoreButton) {
      return (
        <Button
          block
          isLoading={therapistsState.reviewsList && therapistsState.reviewsList.isLoading}
          onClick={loadMoreReviews}
          theme={ButtonThemeTypes.grey}
          type="button"
          size={ButtonSizeTypes.small}
        >
          {i18n._x('Load more', 'Therapist profile load more reviews button')}
        </Button>
      );
    }

    return null;
  }

  render() {
    const {i18n} = this.context;
    const {
      location,
      state,
      therapistsState,
    } = this.props;
    const {
      currentTabMobile,
      factsIconSizes,
      isDesktopMode,
      loadMoreButtonContent,
      onChangeTab,
      onClickClearFilters,
      onClickBookWorker,
      showReviews,
      showReviewsTabMobile,
      supportedHrefLangs,
    } = this;
    const {search} = location;

    const therapistsLink = `/${i18n.lc}/therapists${search}`;

    let pageTitle = state && state.instance ?
      i18n.__react('%(therapistName)s - Practitioner', {
        therapistName: state.instance.name.split(' ')[0],
      }) :
      i18n._x('Practitioner', 'Default therapist meta title');

    if (pageTitle && typeof pageTitle !== 'string') pageTitle = pageTitle.join('');

    const pageDescription = state && state.instance ?
      state.instance.bioShort :
      i18n._x('A Practitioner', 'Default therapist meta description');

    if (!state || (state && state.isLoading) || therapistsState.isProfileLoading) {
      return (
        <div className="therapist-single">
          <DocumentTitle title={pageTitle} />
          <DocumentMeta
            title={pageTitle}
            description={pageDescription}
            hrefLangs={supportedHrefLangs}
          />
          <TherapistSingleLoading />
        </div>
      );
    }

    if (
      (state &&
      !state.isLoading &&
      !therapistsState.isProfileLoading &&
      !therapistsState.workerDetails.instance
      )
    ) {
      return (
        <NotFound location={location} />
      );
    }

    return (
      <div className="therapist-single">
        <DocumentTitle title={pageTitle} />
        <DocumentMeta
          title={pageTitle}
          description={pageDescription}
          hrefLangs={supportedHrefLangs}
        />
        {therapistsState.workerDetails && therapistsState.workerDetails.instance && (
          <div className="therapist-single__container">
            <ProfileHeading
              alt=""
              centeredBreakpoint={THERAPIST_DESKTOP_BREAKPOINT}
              url={`/api/workers/${state.instance.id}/image?crop=fill&width=250&height=250`}
            >
              <BreadcrumbsContainer>
                <BreadcrumbItem
                  color={BreadcrumbColorTheme.white}
                  text={i18n._x('Home', 'Homepage URL breadcrumb')}
                  href={`/${i18n.lc}/`}
                />
                <BreadcrumbItem
                  color={BreadcrumbColorTheme.white}
                  text={i18n._x('Practitioners', 'Therapists URL breadcrumb')}
                  href={`${therapistsLink}`}
                />
                <BreadcrumbItem
                  active
                  color={BreadcrumbColorTheme.white}
                  text={state.instance.name}
                />
              </BreadcrumbsContainer>
            </ProfileHeading>
            <div className="container">
              <div className="row">
                <div className="col-sm-12 col-md-4 col-md-offset-0 col-lg-4 therapist-single__left-column">
                  <TherapistTopInfo
                    isDesktopMode={isDesktopMode}
                    onClickBook={onClickBookWorker}
                    trackingCategory="Therapists profile"
                    workerId={state.instance.id}
                  />
                  <div className="therapist-single__tab-menu-container show-mobile">
                    <div className="therapist-single__tab-menu" id="js-tabs-menu-container">
                      <TherapistProfileTabs
                        activeTab={currentTabMobile}
                        onChangeTab={onChangeTab}
                        showReviewsTabMobile={showReviewsTabMobile}
                        textReviewsEnabled={showReviews}
                      />
                    </div>
                    <Divider
                      marginBottom="0"
                      marginTop="0"
                      size="12"
                    />
                  </div>
                  {/* TODO Add icon to content box */}
                  <div className="therapist-single__certified-box">
                    <ContentBox
                      bordered
                      icon={<Certified size={IconSizes.medium} />}
                      title={i18n._x('Fully insured and certified', 'Therapist details page')}
                      theme={ContentBoxColorThemes.white}
                    />
                  </div>
                  <div className="therapist-single__pinned-reviews-list">
                    <TextReviews
                      bordered
                      reviews={therapistsState.workerPromotedReviewsList}
                      smallStars
                      workerId={state.instance.id}
                    />
                  </div>
                </div>
                <div className="col-sm-12 col-md-6 col-md-offset-1 col-lg-6 col-lg-offset-1">
                  {(isDesktopMode || (!isDesktopMode && currentTabMobile === INFO_TAB_NAME)) && (
                    <TherapistFactsGrid
                      iconSize={factsIconSizes}
                    />
                  )}
                  {isDesktopMode && (
                    <Divider marginBottom="6" marginTop="6" />
                  )}
                  {(isDesktopMode || (!isDesktopMode && currentTabMobile === BIO_TAB_NAME)) && (
                    <section
                      className="row therapist-single__section therapist-single__section--mobile-padding"
                    >
                      <Typography className="therapist-single__desktop-title" component={TypeVariants.h2} variant={TypeVariants.h3}>
                        {i18n._x('Bio', 'Therapist single page')}
                      </Typography>

                      {therapistsState.workerBio && therapistsState.workerBio.map((paragraph, index) => (
                        <Typography margin="0 0 1.6rem" theme="mediumBody" variant={TypeVariants.bodyLarge} key={`bio-paragraph-${index}`}>
                          {paragraph}
                        </Typography>
                      ))}
                    </section>
                  )}
                  {isDesktopMode && (
                    <Divider marginBottom="6" marginTop="6" />
                  )}
                  {(isDesktopMode || (!isDesktopMode && currentTabMobile === REVIEWS_TAB_NAME)) && (
                    <section
                      className="row therapist-single__section therapist-single__reviews-section"
                    >
                      <WorkerReviewsLong
                        isLoading={therapistsState.reviewsList && therapistsState.reviewsList.isLoading}
                        isMobile={!isDesktopMode}
                        key="therapist-single-desktop-reviews"
                        onClearFilters={onClickClearFilters}
                        showPinnedReviews={!isDesktopMode}
                        textReviewsEnabled={showReviews}
                      />
                      {loadMoreButtonContent}
                    </section>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default TherapistSingle;



// WEBPACK FOOTER //
// ./spa/Pages/Therapists/TherapistSingle.jsx